import React, { useState } from "react";
import { createPortal } from "react-dom";
import { X, Check, Plus, Bookmark, Trash2 } from "lucide-react";
import { useTranslation } from "../context/LanguageContext";
import { addSavedKeyword, deleteSavedKeyword, isKeywordSaved } from "../utils/savedKeywords";

interface SavedKeywordsButtonProps {
  searchTerm: string;
  savedKeywords?: string[];
  keywordMatchCounts?: Record<string, number>;
  onSavedKeywordsChange?: (keywords: string[]) => Promise<void> | void;
  selectedSavedKeywords?: string[];
  onSelectedSavedKeywordsChange?: (keywords: string[]) => void;
}

/** Bookmark button + bottom sheet for saved search keywords. Lives inside the feed search row. */
const SavedKeywordsButton: React.FC<SavedKeywordsButtonProps> = ({
  searchTerm,
  savedKeywords = [],
  keywordMatchCounts = {},
  onSavedKeywordsChange,
  selectedSavedKeywords = [],
  onSelectedSavedKeywordsChange,
}) => {
  const { t } = useTranslation();
  const [isKeywordsModalOpen, setIsKeywordsModalOpen] = useState(false);

  const normalizedSearchTerm = searchTerm.trim().replace(/\s+/g, " ");
  const canSaveCurrentSearchKeyword =
    normalizedSearchTerm.length > 0 && !isKeywordSaved(normalizedSearchTerm, savedKeywords);
  const activeKeywordCount = selectedSavedKeywords.length;
  const allKeywordsSelected =
    savedKeywords.length > 0 && selectedSavedKeywords.length === savedKeywords.length;

  const handleToggleSavedKeyword = (keyword: string) => {
    const isSelected = selectedSavedKeywords.some(
      (item) => item.toLowerCase() === keyword.toLowerCase()
    );
    onSelectedSavedKeywordsChange?.(
      isSelected
        ? selectedSavedKeywords.filter((item) => item.toLowerCase() !== keyword.toLowerCase())
        : [...selectedSavedKeywords, keyword]
    );
  };

  const handleSaveCurrentSearchKeyword = async () => {
    if (!canSaveCurrentSearchKeyword) return;
    const nextKeywords = addSavedKeyword(savedKeywords, normalizedSearchTerm);
    await onSavedKeywordsChange?.(nextKeywords);
    if (!selectedSavedKeywords.some((item) => item.toLowerCase() === normalizedSearchTerm.toLowerCase())) {
      const storedKeyword = nextKeywords.find(
        (keyword) => keyword.toLowerCase() === normalizedSearchTerm.toLowerCase()
      );
      if (storedKeyword) {
        onSelectedSavedKeywordsChange?.([...selectedSavedKeywords, storedKeyword]);
      }
    }
  };

  const handleDeleteSavedKeyword = async (keyword: string) => {
    const nextKeywords = deleteSavedKeyword(savedKeywords, keyword);
    await onSavedKeywordsChange?.(nextKeywords);
    onSelectedSavedKeywordsChange?.(
      selectedSavedKeywords.filter((item) => item.toLowerCase() !== keyword.toLowerCase())
    );
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsKeywordsModalOpen(true)}
        className={`relative flex h-7 w-7 flex-none items-center justify-center rounded-full transition-colors ${
          activeKeywordCount > 0
            ? "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300"
            : "text-gray-400 hover:text-gray-600 dark:text-dark-400 dark:hover:text-dark-200"
        }`}
        title={t("products.savedKeywordsTitle")}
        aria-label={t("card.openSavedKeywords")}
      >
        <Bookmark size={15} />
        {activeKeywordCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-[14px] min-w-[14px] items-center justify-center rounded-full bg-primary-600 px-1 text-[9px] font-bold text-white">
            {activeKeywordCount}
          </span>
        )}
      </button>

      {isKeywordsModalOpen && createPortal(
      <div
        className="fixed inset-0 z-[9999] bg-black/30 dark:bg-black/60 sheet-backdrop-in"
        onClick={() => setIsKeywordsModalOpen(false)}
      >
        <div
          className="fixed inset-x-0 bottom-0 bg-white dark:bg-dark-900 rounded-t-2xl w-full max-w-lg mx-auto shadow-2xl sheet-panel-in"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 bg-gray-200 dark:bg-dark-600 rounded-full" />
          </div>
          <div className="px-5 pb-3 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="font-bold text-gray-900 dark:text-white text-base">
                {t("products.savedKeywordsTitle")}
              </h3>
              <p className="mt-1 text-[11px] text-gray-500 dark:text-dark-400">
                {t("products.tapKeywordsToSearch")}
              </p>
            </div>
            <button
              onClick={() => setIsKeywordsModalOpen(false)}
              className="p-1.5 text-gray-400 dark:text-dark-400 hover:text-gray-600 dark:hover:text-dark-200 rounded-full transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="px-5 pb-2 space-y-2">
            {canSaveCurrentSearchKeyword && (
              <button
                onClick={() => void handleSaveCurrentSearchKeyword()}
                className="w-full flex items-center justify-between gap-3 rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 px-3 py-2.5 text-left"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Bookmark className="h-4 w-4 text-purple-600 dark:text-purple-300" />
                  <span className="text-[13px] font-semibold text-purple-700 dark:text-purple-300 truncate">
                    {t("card.saveKeyword", { term: normalizedSearchTerm })}
                  </span>
                </div>
                <Plus className="h-4 w-4 text-purple-600 dark:text-purple-300 flex-shrink-0" />
              </button>
            )}

            {savedKeywords.length > 0 && (
              <div className="flex items-center gap-3 text-xs font-semibold text-primary-600 dark:text-primary-400">
                <button
                  onClick={() =>
                    onSelectedSavedKeywordsChange?.(
                      allKeywordsSelected ? [] : [...savedKeywords]
                    )
                  }
                >
                  {allKeywordsSelected ? t("products.clearAllKeywords") : t("products.selectAllKeywords")}
                </button>
                {selectedSavedKeywords.length > 0 && !allKeywordsSelected && (
                  <button onClick={() => onSelectedSavedKeywordsChange?.([])}>
                    {t("products.clearSelected")}
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="px-3 pb-[max(1rem,env(safe-area-inset-bottom))] max-h-[58vh] overflow-y-auto">
            {savedKeywords.length > 0 ? (
              savedKeywords.map((keyword) => {
                const isSelected = selectedSavedKeywords.some(
                  (item) => item.toLowerCase() === keyword.toLowerCase()
                );
                const matchCount = keywordMatchCounts[keyword] ?? 0;
                return (
                  <div
                    key={keyword}
                    className="flex items-center gap-2 px-2 py-1 border-b border-gray-100 dark:border-dark-800"
                  >
                    <button
                      onClick={() => handleToggleSavedKeyword(keyword)}
                      className={`flex-1 min-w-0 flex items-center gap-2 rounded-xl px-2.5 py-2 text-left transition-colors ${
                        isSelected
                          ? "bg-purple-50 dark:bg-purple-900/20"
                          : "hover:bg-gray-50 dark:hover:bg-dark-800"
                      }`}
                    >
                      <div
                        className={`w-[18px] h-[18px] rounded-full border flex items-center justify-center flex-shrink-0 ${
                          isSelected
                            ? "bg-primary-600 border-primary-600"
                            : "border-gray-300 dark:border-dark-500"
                        }`}
                      >
                        {isSelected && <Check size={12} className="text-white" />}
                      </div>
                      <span
                        className={`flex-1 truncate text-[13px] font-semibold ${
                          isSelected
                            ? "text-purple-700 dark:text-purple-300"
                            : "text-gray-800 dark:text-dark-100"
                        }`}
                      >
                        {keyword}
                      </span>
                      <span
                        className={`min-w-[30px] h-[22px] px-2 rounded-full text-[11px] font-bold flex items-center justify-center ${
                          isSelected
                            ? "bg-primary-600 text-white"
                            : "bg-gray-100 dark:bg-dark-700 text-gray-500 dark:text-dark-300"
                        }`}
                      >
                        {matchCount}
                      </span>
                    </button>
                    <button
                      onClick={() => void handleDeleteSavedKeyword(keyword)}
                      className="w-7 h-7 flex items-center justify-center text-red-500 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      aria-label={t("card.deleteKeyword", { keyword })}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-10 text-center">
                <Bookmark className="h-6 w-6 mx-auto text-gray-300 dark:text-dark-600" />
                <p className="mt-3 text-sm font-semibold text-gray-800 dark:text-dark-100">
                  {t("products.noSavedKeywordsYet")}
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-dark-400">
                  {t("products.saveSearchReuse")}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>,
      document.body
    )}
    </>
  );
};

export default SavedKeywordsButton;
